import * as ngCore from '@angular/core'
import { isDeepEqual } from '@attain-apps/data-utils'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { PartialId } from '@attain-apps/string-builder'
import { TypeChecker } from '@attain-apps/type-guards'

import * as state from '../../../../../../../state'

import * as components from './components'

/** Input component for DMS reference values */
@ngCore.Component({
  imports: [
    components.addRecordDialog.Component,
    ds.input.select.single.Component,
    ds.input.select.multiple.Component,
  ],
  selector: 'atn-reference-input',
  styleUrl: './component.scss',
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link state.dms.schema.Feature} */
  private readonly _schemaFeature = ngCore.inject(state.dms.schema.Feature)
  /** @see {@link state.display.referenceRecords.Feature} */
  private readonly _displayFeature = ngCore.inject(
    state.display.referenceRecords.Feature,
  )
  /** @see {@link state.dms.records.Collection,} */
  public readonly recordsEc = ngCore.inject(state.dms.records.Collection)
  /** @see {@link ents.core.dms.data.Value.Type} */
  public readonly typeSg =
    ngCore.input.required<ents.core.dms.data.Value.Type.Reference>({
      alias: 'type',
    })
  /** Dimension id derived from {@link typeSg} */
  private readonly _dimensionIdSg = ngCore.computed(
    (type = this.typeSg()) => type.dimensionId,
  )
  /** Optional label shown above the input */
  public readonly labelSg = ngCore.input<string>(undefined, {
    alias: 'label',
  })
  /** Whether a selection is required */
  public readonly requiredSg = ngCore.input<boolean>(false, {
    alias: 'required',
  })
  /** @see {@link ents.core.dms.data.Value.Reference.Default} */
  public readonly valueSg = ngCore.model<
    // NOTE: this may be undefined during initial reference value creation,
    // as nothing has been set up yet
    | ents.core.dms.data.Value.Reference.Default
    | ents.core.dms.data.Value.Reference.InFormat.Dimension
    | undefined
  >(undefined, {
    alias: 'value',
  })
  /**
   * When the incoming {@link valueSg} is in
   * {@link ents.core.dms.data.Value.Reference.InFormat.Dimension dimension format},
   * this signal holds the raw dimension map so that
   * {@link patchPartialValue} can pre-fill the record when the user switches
   * entities.
   */
  private readonly _dimensionMapSg = ngCore.computed(
    (value = this.valueSg()) =>
      ents.core.dms.data.Value.Reference.InFormat.Dimension.isDimension(value)
        ? (value as ents.core.dms.data.Value.Reference.InFormat.Dimension)
        : undefined,
  )
  /**
   * Partial value is used as "temporary" {@link valueSg} until the user
   * selects the entity and record.
   */
  public readonly partialValueSg = ngCore.linkedSignal<
    Partial<ents.core.dms.data.Value.Reference.Default> | undefined
  >(
    (value = this.valueSg(), dimensionId = this._dimensionIdSg()) => {
      // Entity format: { entityId, recordId }
      // Resolve entityId to full Entity.Id (backend may return PartialId)
      if (ents.core.dms.data.Value.Reference.Default.isDefault(value)) {
        return {
          entityId: _getEntityId(value.entityId, dimensionId),
          recordId: value.recordId,
        }
      }

      // Dimension format: { '!campus': '?1', '!bldg': '?1.160', ... }
      // Resolve the lowest-level (leaf) entity that has a non-null record
      if (
        ents.core.dms.data.Value.Reference.InFormat.Dimension.isDimension(value)
      ) {
        const entities = this._entitiesSg()
        // Iterate from leaf to root to find the deepest entity with a value
        for (let i = entities.length - 1; i >= 0; i--) {
          const entity = entities[i]
          const partialId = ents.core.dms.models.Id.Parts.from(
            entity.id,
          ).partialId
          const recordId = value[partialId]
          if (TypeChecker.isString(recordId)) {
            // Use full Entity.Id so the entity dropdown can match its items
            return { entityId: entity.id, recordId }
          }
        }
      }

      return {
        entityId: this.defaultEntityIdSg(),
      }
    },
    // NOTE: we are using equality function just because how we set partialValue
    // in patchPartialValue method (we create new object there)
    { equal: isDeepEqual },
  )
  /**
   * The {@link ents.core.dms.models.Entity.Id entityId} from the
   * {@link partialValueSg partial value}.
   */
  private readonly _partialValueEntityIdSg = ngCore.computed(
    (partialValue = this.partialValueSg()) => partialValue?.entityId,
  )
  /**
   * The {@link ents.core.dms.models.Entity entities} in the
   * {@link ents.core.dms.models.Dimension dimension} pulled out from
   * {@link ents.core.dms.data.Value.Type.Reference reference}.
   */

  /**
   * If displayEntity and entId are the same show add record dialog
   */
  public readonly showAddDialogSg = ngCore.computed(
    (
      entId = this._partialValueEntityIdSg(),
      editedEntIds = this._displayFeature.selectors.editedEntityIds.asSignal(),
    ) =>
      ents.core.dms.models.Entity.Id.isId(entId)
        ? editedEntIds.includes(entId)
        : false,
  )
  /** Entities in the referenced dimension, sorted root-to-leaf */
  private readonly _entitiesSg = ngCore.computed(
    (
      reference = this.typeSg(),
      schema = this._schemaFeature.selectors.schema.asSignal(),
    ) => schema.getEntitiesInDimension(reference.dimensionId),
  )
  /**
   * The default {@link ents.core.dms.models.Entity entity} to be selected
   * when there is no entity selected
   */
  private readonly _defaultEntitySg = ngCore.computed<
    // NOTE: When accessing the first element [0] of a potentially empty array,
    // it might return undefined. We need to explicitly type this as TypeScript
    // doesn't infer the type correctly in this case
    ents.core.dms.models.Entity | undefined
  >((entities = this._entitiesSg()) => entities[0])
  /**
   * The {@link ents.core.dms.models.Entity.Id entityId} of the
   * {@link _defaultEntitySg}.
   */
  public readonly defaultEntityIdSg = ngCore.computed(
    (defaultEntity = this._defaultEntitySg()) => defaultEntity?.id,
  )
  /**
   * Same as {@link _entitiesSg} but in the form of {@link ds.input.select.models.Item}.
   */
  public readonly entityItemsSg = ngCore.computed(
    (entities = this._entitiesSg()) =>
      entities.map(
        ({ id: value, name: label }) =>
          ({ label, value }) satisfies ds.input.select.models.Item,
      ),
  )
  /**
   * Determines if the referenced dimension in {@link typeSg} contains
   * multiple {@link ents.core.dms.models.Entity entities}.
   */
  public readonly moreThenOneEntitySg = ngCore.computed(
    (entities = this._entitiesSg()) => entities.length > 1,
  )

  /**
   * The {@link ents.core.dms.data.Record records} of the currently {@link selectedEntityIdSg selected}
   * {@link ents.core.dms.models.Entity entity}.
   */
  private readonly _recordsSg = ngCore.computed(
    (
      { entityId } = this.partialValueSg() ?? {},
      dimensionId = this._dimensionIdSg(),
    ) =>
      entityId
        ? this._displayFeature.selectors
            .getRecords({
              entityId: _getEntityId(entityId, dimensionId),
            })
            .asSignal()
        : [],
  )
  /**
   * Indicates if the {@link _recordsSg records} are being loaded for the current entity.
   * Returns true if either the schema is loading (needed to determine the entityId)
   * or if the records themselves are being fetched.
   */
  public readonly recordsLoadingSg = ngCore.computed(
    (
      { entityId } = this.partialValueSg() ?? {},
      dimensionId = this._dimensionIdSg(),
      isSchemaLoading = this._schemaFeature.selectors.isLoading.asSignal(),
    ) =>
      // if schema is loading, we immediately return true
      isSchemaLoading
        ? true
        : // if we have an entityId, we check if the records are loading
          entityId
          ? this._displayFeature.selectors
              .isLoading({
                entityId: _getEntityId(entityId, dimensionId),
              })
              .asSignal()
          : // if non of the above conditions are met, we return false
            false,
  )
  /**
   * Indicates whether the {@link _recordsSg records} for the current {@link selectedEntityIdSg selected}
   * {@link ents.core.dms.models.Entity entity} have encountered an error.
   */
  public readonly recordsErrorSg = ngCore.computed(
    (
      { entityId } = this.partialValueSg() ?? {},
      dimensionId = this._dimensionIdSg(),
    ) =>
      entityId
        ? this._displayFeature.selectors
            .getError({
              entityId: _getEntityId(entityId, dimensionId),
            })
            .asSignal()
        : undefined,
  )
  /**
   * Same as {@link _recordsSg} but in the form of {@link ds.input.select.models.Item}.
   */
  public readonly recordItemsSg = ngCore.computed(
    (
      records = this._recordsSg(),
      entities = this._entitiesSg(),
      partialValue = this.partialValueSg(),
    ) => {
      const items = records
        .map<ds.input.select.models.Item>(record => ({
          label:
            state.display.referenceRecords.models.Record.toBreadcrumb(record),
          value: record.id,
        }))
        .filter(TypeChecker.isNotNullish.raw)

      const entity = entities.find(ent => ent.id === partialValue?.entityId)
      return entity
        ? [
            {
              label: 'Add new record...',
              onSelect: () => {
                this._displayFeature.actions.addRecord
                  .from('Reference.Input.addNewRecord.Clicked')
                  .dispatch({ entityId: entity.id })
              },
            },
            ...items,
          ]
        : items
    },
  )

  /**
   * Tracks whether {@link partialValueSg} was modified by user interaction
   * (via {@link patchPartialValue}).  The constructor effect uses this flag
   * to avoid emitting `undefined` through `(valueChange)` during the
   * first render — before the store has resolved the incoming raw record
   * value into a structured `{ entityId, recordId }`.  Without this guard
   * every reference-type attribute (e.g. Room → Room Type) would dispatch
   * a spurious editing-plugin `edit` action that clears the field.
   */
  private _partialValueModifiedByUser = false

  constructor() {
    ngCore.effect(() => {
      const { entityId, recordId } = this.partialValueSg() ?? {}

      if (entityId && recordId) {
        this.valueSg.set({ entityId, recordId })
      } else if (this._partialValueModifiedByUser) {
        this.valueSg.set(undefined)
      }
    })

    ngCore.effect(() => {
      const entityId = this._partialValueEntityIdSg()

      if (entityId) {
        const dimensionId = this._dimensionIdSg()
        this._displayFeature.actions.loadRecords
          .from('display.value.input.reference.Init (Component)')
          .dispatch({
            cacheFirst: true,
            entityId: _getEntityId(entityId, dimensionId),
          })
      }
    })
  }

  /** Re-fetches records for the currently selected entity */
  public entityRefetchBtnClickHandler(): void {
    const entityId = this._partialValueEntityIdSg()

    if (entityId) {
      const dimensionId = this._dimensionIdSg()
      this._displayFeature.actions.loadRecords
        .from(
          'display.value.input.reference.Refetch.Button.Clicked (Component)',
        )
        .dispatch({
          entityId: _getEntityId(entityId, dimensionId),
        })
    }
  }

  /**
   * Patches the {@link partialValueSg} with the given entity and/or record.
   * When only `entityId` is provided (entity switch) and the incoming value
   * was in dimension format, the record is pre-filled from the
   * {@link _dimensionMapSg dimension map}.
   */
  public patchPartialValue({
    entityId,
    recordId,
  }: Partial<ents.core.dms.data.Value.Reference.Default>) {
    this._partialValueModifiedByUser = true
    // Entity switch: recordId not provided — look up from dimension map
    if (recordId === undefined) {
      const dimensionMap = this._dimensionMapSg()
      if (entityId && dimensionMap) {
        // Try to pre-fill the record from the dimension map
        const partialId = PartialId.isPartialId(entityId)
          ? entityId
          : ents.core.dms.models.Id.Parts.from(entityId).partialId
        const mappedRecordId = dimensionMap[partialId]
        if (TypeChecker.isString(mappedRecordId)) {
          this.partialValueSg.set({ entityId, recordId: mappedRecordId })
          return
        }
      }
      this.partialValueSg.update(() => ({ entityId }))
      return
    }

    // if entityId doesn't exist, update it to undefined
    // This, helps if we want to set it back to undefined
    if (entityId === undefined) {
      this.partialValueSg.update(old => ({ recordId: old?.recordId }))
      return
    }

    this.partialValueSg.update(old => {
      entityId ??= old?.entityId
      recordId ??= old?.recordId

      return entityId && recordId ? { entityId, recordId } : undefined
    })
  }

  /** {@link ds.input.select.single.Component} emits unknown value on value change so we need to make sure it is partialId or undefined */
  public selectValueChangeHandler(recordId: unknown) {
    const isPartialOrUndefined =
      PartialId.isPartialId(recordId) || TypeChecker.isUndefined(recordId)
    if (isPartialOrUndefined) {
      this.patchPartialValue({
        entityId: this.partialValueSg()?.entityId,
        recordId,
      })
    }
  }
}

// NOTE: makes our code a little bit dryer, because we don't need to repeat this on multiple places
/**
 * Takes an entityId (full or partial) and dimensionId to construct a complete entityId.
 */
function _getEntityId(
  entityId: ents.core.dms.models.Entity.Id | PartialId,
  dimensionId: ents.core.dms.models.Dimension.Id,
): ents.core.dms.models.Entity.Id {
  return PartialId.isPartialId(entityId)
    ? ents.core.dms.models.Entity.Id.from({
        context: ents.core.dms.models.Entity.Id.Context.from(dimensionId),
        partialId: entityId,
        type: ents.core.dms.models.Type.Entity,
      })
    : entityId
}
